import React, { useMemo, useState } from 'react';
import './OrdersList.css';
import Badge from 'react-bootstrap/Badge';
import { NumericFormat } from 'react-number-format';

const STATUS_TERMINAL = new Set(['filled', 'canceled', 'cancelled', 'canceled_by_user', 'rejected', 'expired', 'error']);
const STATUS_WORKING = new Set([
  'open',
  'new',
  'submitted',
  'pending',
  'accepted',
  'working',
  'queued',
  'partially_filled',
  'unprocessed',
  'cancelling',
  'pending_cancel',
]);

const STATUS_FILTERS = [
  { value: 'open', label: 'Open' },
  { value: 'all', label: 'All' },
  { value: 'filled', label: 'Filled' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'rejected', label: 'Rejected' },
];

function firstDefined(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return null;
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatDate(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function normalizeStatus(order) {
  return String(order?.status || 'unknown').toLowerCase();
}

function getOrderSymbol(order) {
  return firstDefined(order?.symbol, order?.asset?.symbol, 'Unknown');
}

function getOrderPrice(order) {
  return firstDefined(order?.avg_fill_price, order?.price, order?.limit_price, order?.stop_price);
}

function getOrderTime(order) {
  return firstDefined(
    order?.timestamp,
    order?.broker_update_date,
    order?.broker_create_date,
    order?.updated_at,
    order?.created_at,
    order?.submitted_at
  );
}

function getOrderType(order) {
  return String(firstDefined(order?.order_type, order?.type, 'order')).replace(/_/g, ' ');
}

function getStatusClass(status) {
  if (STATUS_TERMINAL.has(status)) return `orders-status-terminal orders-status-${status}`;
  if (STATUS_WORKING.has(status)) return `orders-status-working orders-status-${status}`;
  return 'orders-status-unknown';
}

function getOrderKey(order, index) {
  return [
    order?.order_id,
    order?.symbol,
    order?.status,
    order?.timestamp,
    index,
  ].filter(Boolean).join('|') || `order-${index}`;
}

export default function OrdersList({
  orders,
  isLoading = false,
  error = '',
  lastUpdated = null,
  source = '',
}) {
  const [sortField, setSortField] = useState('time');
  const [sortAsc, setSortAsc] = useState(false);
  const [statusFilter, setStatusFilter] = useState('open');

  const rows = useMemo(() => (Array.isArray(orders) ? orders : []), [orders]);

  const filteredRows = useMemo(() => {
    const filtered = rows.filter((order) => {
      const status = normalizeStatus(order);
      if (statusFilter === 'open') return STATUS_WORKING.has(status);
      if (statusFilter === 'filled') return status === 'filled';
      if (statusFilter === 'canceled') return status === 'canceled' || status === 'cancelled' || status === 'canceled_by_user';
      if (statusFilter === 'rejected') return status === 'rejected' || status === 'expired' || status === 'error';
      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortField === 'symbol') {
        return String(getOrderSymbol(a)).localeCompare(String(getOrderSymbol(b)));
      }
      if (sortField === 'side') {
        return String(firstDefined(a?.action, '')).localeCompare(String(firstDefined(b?.action, '')));
      }
      if (sortField === 'status') {
        return normalizeStatus(a).localeCompare(normalizeStatus(b));
      }
      if (sortField === 'quantity') {
        return (toNumber(a?.quantity) ?? 0) - (toNumber(b?.quantity) ?? 0);
      }
      if (sortField === 'price') {
        return (toNumber(getOrderPrice(a)) ?? 0) - (toNumber(getOrderPrice(b)) ?? 0);
      }
      if (sortField === 'label') {
        return String(firstDefined(a?.reason, a?.tag, '')).localeCompare(String(firstDefined(b?.reason, b?.tag, '')));
      }
      const aTime = new Date(getOrderTime(a) || 0).getTime() || 0;
      const bTime = new Date(getOrderTime(b) || 0).getTime() || 0;
      return aTime - bTime;
    });

    return sortAsc ? sorted : sorted.reverse();
  }, [rows, sortAsc, sortField, statusFilter]);

  const handleSortClick = (field) => {
    if (sortField === field) {
      setSortAsc((current) => !current);
      return;
    }
    setSortField(field);
    setSortAsc(field !== 'time');
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortAsc ? ' ▲' : ' ▼';
  };

  const renderSortableHeader = (field, label, className = '') => (
    <th className={className}>
      <button
        type="button"
        className="orders-table-sort-header"
        onClick={() => handleSortClick(field)}
        aria-label={`Sort orders by ${label}`}
      >
        {label}{sortIndicator(field)}
      </button>
    </th>
  );

  const selectedStatusLabel = STATUS_FILTERS.find((filter) => filter.value === statusFilter)?.label || 'Orders';
  const orderSummaryLabel = statusFilter === 'all'
    ? 'broker'
    : `${selectedStatusLabel.toLowerCase()} broker`;

  return (
    <div className="orders-list-robinhood">
      <div className="orders-list-header">
        <div>
          <h3>Orders</h3>
          <p>
            {filteredRows.length} {orderSummaryLabel} {filteredRows.length === 1 ? 'order' : 'orders'}
            {lastUpdated ? `, updated ${formatDate(lastUpdated)}` : ''}
          </p>
        </div>
        <div className="orders-filter-control">
          <label htmlFor="orders-status-filter">Status</label>
          <select
            id="orders-status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            {STATUS_FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="orders-error">{error}</div>}

      {!isLoading && filteredRows.length === 0 ? (
        <div className="orders-empty">
          {statusFilter === 'open' ? 'No open orders right now.' : 'No orders match this status.'}
        </div>
      ) : (
        <div className="orders-table-scroll">
          <table className="orders-detail-table">
            <thead>
              <tr>
                {renderSortableHeader('symbol', 'Order')}
                {renderSortableHeader('side', 'Side')}
                {renderSortableHeader('quantity', 'Qty', 'numeric')}
                {renderSortableHeader('price', 'Price', 'numeric')}
                {renderSortableHeader('status', 'Status')}
                {renderSortableHeader('time', 'Time')}
                {renderSortableHeader('label', 'Label')}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((order, index) => {
                const status = normalizeStatus(order);
                const price = getOrderPrice(order);
                const quantity = firstDefined(order?.quantity, order?.filled_quantity);
                const label = firstDefined(order?.reason, order?.tag);
                return (
                  <tr key={getOrderKey(order, index)}>
                    <td>
                      <div className="orders-symbol-cell">{getOrderSymbol(order)}</div>
                      <div className="orders-subtext">
                        {getOrderType(order)}
                        {order?.order_id ? ` • ${order.order_id}` : ''}
                      </div>
                    </td>
                    <td>{String(firstDefined(order?.action, 'unknown')).replace(/_/g, ' ')}</td>
                    <td className="numeric">{quantity ?? '-'}</td>
                    <td className="numeric">
                      {price === null ? '-' : (
                        <NumericFormat
                          value={price}
                          displayType="text"
                          thousandSeparator
                          prefix="$"
                          decimalScale={2}
                          fixedDecimalScale
                        />
                      )}
                    </td>
                    <td>
                      <Badge className={`orders-status-badge ${getStatusClass(status)}`}>
                        {status.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="orders-time-cell">{formatDate(getOrderTime(order))}</td>
                    <td className="orders-label-cell">{label || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
